/**
 * Shared presentational building blocks.
 *
 * Small and unopinionated on purpose: the pages own their layout, and these
 * exist so status colours, empty states and spinners look identical everywhere.
 */

import type { ReactNode } from 'react';

// -- Status badges ------------------------------------------------------------

const STATUS_STYLES: Record<string, string> = {
  trained: 'bg-emerald-100 text-emerald-800',
  crawling: 'bg-amber-100 text-amber-800',
  pending: 'bg-slate-100 text-slate-700',
  error: 'bg-red-100 text-red-800',
  open: 'bg-blue-100 text-blue-800',
  closed: 'bg-slate-100 text-slate-600',
  lead: 'bg-indigo-100 text-indigo-800',
  customer: 'bg-emerald-100 text-emerald-800',
  positive: 'bg-emerald-100 text-emerald-800',
  negative: 'bg-red-100 text-red-800',
  emailed: 'bg-emerald-100 text-emerald-800',
  generated: 'bg-blue-100 text-blue-800',
  recommended: 'bg-amber-100 text-amber-800',
  approved: 'bg-sky-100 text-sky-800',
  pushed: 'bg-emerald-100 text-emerald-800',
  dismissed: 'bg-slate-100 text-slate-600',
  youtube: 'bg-red-100 text-red-800',
  // Lead territories.
  us: 'bg-slate-100 text-slate-700',
  us_territory: 'bg-slate-100 text-slate-700',
  canada: 'bg-slate-100 text-slate-700',
  international: 'bg-amber-100 text-amber-800',
  unknown: 'bg-slate-100 text-slate-500',
  // AI Suggestions issue types.
  no_answer: 'bg-red-100 text-red-800',
  partial_answer: 'bg-amber-100 text-amber-800',
  lacks_depth: 'bg-sky-100 text-sky-800',
  wrong_scope: 'bg-violet-100 text-violet-800',
  added: 'bg-emerald-100 text-emerald-800',
  // System check rows.
  ok: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  trained: 'Trained',
  crawling: 'Crawling',
  pending: 'Queued',
  error: 'Error',
  open: 'Open',
  closed: 'Closed',
  lead: 'Lead',
  customer: 'Customer',
  add_to_netsuite: 'Add to NetSuite',
  needs_review: 'Needs review',
  irrelevant: 'Not a lead',
  needs_response_improvement: 'Improve response',
  emailed: 'Emailed',
  generated: 'Generated',
  recommended: 'Awaiting approval',
  approved: 'Approved (not sent)',
  pushed: 'Pushed to NetSuite',
  dismissed: 'Dismissed',
  youtube: 'YouTube video',
  us: 'United States',
  us_territory: 'US territory',
  canada: 'Canada',
  international: 'International',
  unknown: 'No postal code',
  no_answer: 'No Answer',
  partial_answer: 'Partial Answer',
  lacks_depth: 'Lacks Depth',
  wrong_scope: 'Wrong Scope',
  added: 'Added to training',
  ok: 'OK',
  warning: 'Warning',
  failed: 'Failed',
};

export function StatusBadge({ status }: { status: string }): JSX.Element {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_STYLES[status] ?? 'bg-slate-100 text-slate-700'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// -- Buttons ------------------------------------------------------------------

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  className?: string;
}

const BUTTON_VARIANTS = {
  primary: 'bg-brand-blue text-white hover:bg-sky-700 disabled:bg-sky-300',
  secondary:
    'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 disabled:text-slate-400',
  danger: 'bg-white text-red-700 border border-red-300 hover:bg-red-50',
} as const;

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled = false,
  className = '',
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed ${BUTTON_VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

// -- Feedback -----------------------------------------------------------------

export function Spinner({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-blue" />
      {label}
    </div>
  );
}

export function ErrorNotice({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      {message}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="p-10 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
    </div>
  );
}

// -- Layout helpers -----------------------------------------------------------

export function Card({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-16"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`w-full rounded-lg bg-white shadow-xl ${wide ? 'max-w-3xl' : 'max-w-lg'}`}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            ✕
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
